<template>
  <div>
    <el-card shadow="hover">
      <template slot="header">
        <div class="clearfix">
          <span>库存管理</span>
          <div style="float: right">
            <el-button type="success" size="small" @click="handleExport" :loading="exporting">导出Excel</el-button>
          </div>
        </div>
      </template>

      <el-form :inline="true" :model="searchForm" class="mb-4">
        <el-form-item label="商品名称">
          <el-input v-model="searchForm.name" placeholder="请输入" style="width: 180px" @keyup.enter.native="handleSearch"></el-input>
        </el-form-item>
        <el-form-item label="商品分类">
          <el-select v-model="searchForm.categoryId" placeholder="全部" clearable style="width: 160px" @change="handleSearch">
            <el-option v-for="c in categoryList" :key="c.id" :label="c.name" :value="c.id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">搜索</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>

      <el-table :data="pagedList" style="width: 100%" stripe size="small" border @expand-change="onExpandChange">
        <el-table-column type="expand" width="40" fixed="left">
          <template slot-scope="props">
            <div class="detail-panel">
              <div class="detail-panel-title"><i class="el-icon-info"></i><span>库存详情</span></div>
              <div class="detail-card-row">
                <div class="detail-card-body cols-4">
                  <div class="dc-cell"><span class="dc-label">商品ID</span><span class="dc-value">{{ props.row.id }}</span></div>
                  <div class="dc-cell"><span class="dc-label">商品分类</span>
                    <el-tag :type="isGift(props.row.categoryId) ? 'warning' : 'primary'" size="mini">{{ props.row.categoryName || '-' }}</el-tag>
                  </div>
                  <div class="dc-cell"><span class="dc-label">商品名称</span><span class="dc-value">{{ props.row.name || '-' }}</span></div>
                  <div class="dc-cell"><span class="dc-label">实际库存</span>
                    <el-tag :type="props.row.stock <= 0 ? 'danger' : 'success'" size="mini">{{ props.row.stock || 0 }}</el-tag>
                  </div>
                </div>
              </div>
              <!-- 批次库存明细：每条批次 1 个 dc-cell，三段按固定宽度排列 -->
              <div class="detail-panel-title" style="margin-top:14px"><i class="el-icon-collection-tag"></i><span>批次库存明细</span></div>
              <div class="detail-card-row">
                <div class="detail-card-body" style="display:flex;flex-direction:column;gap:6px">
                  <div v-for="(b, i) in (batchesMap[props.row.id] || [])" :key="i" class="dc-cell batch-row">
                    <span class="batch-seg" style="width:200px"><span class="dc-label">序号</span><span class="dc-value">{{ i + 1 }}</span></span>
                    <span class="batch-seg" style="width:400px"><span class="dc-label">生产批次</span><span class="dc-value">{{ b.batchNo }}</span></span>
                    <span class="batch-seg"><span class="dc-label">剩余库存</span>
                      <el-tag :type="Number(b.remainingQuantity || 0) <= 0 ? 'danger' : 'success'" size="mini">{{ b.remainingQuantity }}</el-tag>
                    </span>
                  </div>
                  <div v-if="!(batchesMap[props.row.id] || []).length" class="dc-cell">
                    <span class="muted">该商品暂无批次</span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="商品分类" width="120">
          <template slot-scope="s">
            <el-tag :type="isGift(s.row.categoryId) ? 'warning' : 'primary'" size="mini">{{ s.row.categoryName || '-' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="商品名称" min-width="180" show-overflow-tooltip></el-table-column>
        <el-table-column prop="batches" label="生产批次" min-width="240" show-overflow-tooltip>
          <template slot-scope="s">{{ s.row.batches || '-' }}</template>
        </el-table-column>
        <el-table-column label="实际库存" width="120" align="center">
          <template slot-scope="s">
            <el-tag :type="s.row.stock <= 0 ? 'danger' : 'success'" size="small">{{ s.row.stock || 0 }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="商品现状" width="120" align="center">
          <template slot-scope="s">
            <el-tag :type="s.row.deleted ? 'danger' : 'success'" size="mini">{{ s.row.deleted ? '已删除商品' : '未删除商品' }}</el-tag>
          </template>
        </el-table-column>
      </el-table>

      <div class="mt-4">
        <el-pagination
          @size-change="s => { pageSize = s; currentPage = 1 }"
          @current-change="p => { currentPage = p }"
          :current-page="currentPage" :page-sizes="[10, 20, 50, 100]" :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper" :total="list.length"></el-pagination>
      </div>
    </el-card>
  </div>
</template>

<script>
import { inventoryApi, categoryApi } from '../api/api'

export default {
  name: 'StockManagement',
  data() {
    return {
      list: [],
      categoryList: [],
      searchForm: { name: '', categoryId: null },
      currentPage: 1,
      pageSize: 10,
      exporting: false,
      batchesMap: {}
    }
  },
  computed: {
    pagedList() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.list.slice(start, start + this.pageSize)
    }
  },
  mounted() {
    this.loadCategories()
    // 支持外部跳入时携带关键字（个人待办点击产品行 → /stock?keyword=xxx）
    const kw = this.$route && this.$route.query && this.$route.query.keyword
    if (kw) this.searchForm.name = String(kw)
    this.loadList()
  },
  methods: {
    loadCategories() {
      categoryApi.getCategoryList().then(res => { this.categoryList = res.data || [] })
    },
    isGift(categoryId) {
      const c = this.categoryList.find(x => x.id === categoryId)
      return !!(c && ['Gift', '礼品'].includes(c.name))
    },
    onExpandChange(row, expandedRows) {
      const isOpen = (expandedRows || []).some(r => r && r.id === row.id)
      if (!isOpen) return
      if (this.batchesMap[row.id]) return
      inventoryApi.getBatches(row.id).then(res => {
        const raw = Array.isArray(res.data) ? res.data : []
        const list = raw.map(x => typeof x === 'string'
          ? { batchNo: x, remainingQuantity: 0 }
          : { batchNo: x.batchNo, remainingQuantity: Number(x.remainingQuantity || 0) })
        this.$set(this.batchesMap, row.id, list)
      }).catch(() => { this.$set(this.batchesMap, row.id, []) })
    },
    loadList() {
      inventoryApi.getAllStock(this.searchForm.name || '', this.searchForm.categoryId || null).then(res => {
        this.list = res.data || []
        this.currentPage = 1
      })
    },
    handleSearch() { this.loadList() },
    resetSearch() { this.searchForm = { name: '', categoryId: null }; this.loadList() },
    handleExport() {
      this.exporting = true
      inventoryApi.exportAllStock(this.searchForm.name || '', this.searchForm.categoryId || null).then(res => {
        const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = '库存_' + new Date().toISOString().slice(0, 10) + '.xlsx'
        link.click()
      }).finally(() => { this.exporting = false })
    }
  }
}
</script>

<style scoped>
.mb-4 { margin-bottom: 16px; }
.mt-4 { margin-top: 16px; text-align: right; }

.detail-panel { padding: 14px 24px 18px; background: linear-gradient(180deg,#fafcff 0%,#f5f7fa 100%); border-top: 1px dashed #dcdfe6; }
.detail-panel-title { display: flex; align-items: center; gap: 6px; color: #303133; font-size: 13px; font-weight: 600; margin: 4px 0 12px; }
.detail-panel-title i { color: #409eff; }
.detail-card-row { background: #fff; border: 1px solid #ebeef5; border-radius: 6px; box-shadow: 0 1px 2px rgba(0,0,0,0.02); padding: 12px 14px; margin-bottom: 12px; }
.detail-card-body { display: grid; gap: 8px; }
.detail-card-body.cols-4 { grid-template-columns: repeat(4, 1fr); }
.detail-card-body.cols-3 { grid-template-columns: repeat(3, 1fr); }
.detail-card-body.batch-grid { grid-template-columns: 100px 1fr 1fr; }
.batch-seg { display: inline-flex; align-items: center; gap: 8px; }
.dc-cell { display: flex; justify-content: flex-start; align-items: center; gap: 8px; padding: 6px 10px; background: #f8f9fb; border-radius: 4px; }
.dc-cell.w4 { grid-column: span 4; }
.dc-cell.w3 { grid-column: span 3; }
.dc-label { color: #909399; font-size: 12px; flex-shrink: 0; }
.dc-label::after { content: '：'; }
.dc-value { color: #303133; font-size: 13px; font-weight: 500; }
</style>
